import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, MapPin, Calendar, ArrowRight, Play } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const videoRefs = useRef({});

  useEffect(() => {
    // SEO title
    document.title = "Galerie de Véhicules | Fast&Car - Nos réalisations";

    const fetchData = async () => {
      try {
        const [vRes, videoRes] = await Promise.all([
          axios.get(`${API}/vehicles`),
          axios.get(`${API}/gallery-videos`),
        ]);
        setVehicles(vRes.data);
        setVideos(videoRes.data);
      } catch (error) {
        console.error("Error fetching:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % vehicles.length);
  }, [vehicles.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + vehicles.length) % vehicles.length);
  };

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying || vehicles.length === 0) return;
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, vehicles.length, nextSlide]);

  const getOriginFlag = (origin) => {
    const flags = {
      "Allemagne": "🇩🇪",
      "Belgique": "🇧🇪",
      "Corée du Sud": "🇰🇷",
      "Italie": "🇮🇹",
      "Espagne": "🇪🇸",
      "France": "🇫🇷"
    };
    return flags[origin] || "🌍";
  };

  return (
    <div data-testid="gallery-page" className="pt-32 md:pt-40">
      {/* Hero Section with Carousel */}
      <section className="relative min-h-[80vh] bg-carbon-bg overflow-hidden">
        {/* Carbon texture overlay */}
        <div 
          className="absolute inset-0 opacity-20 pointer-events-none z-0" 
          style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} 
        />
        
        {/* Carousel */}
        {!loading && vehicles.length > 0 && (
          <div 
            className="relative h-[80vh]"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            {/* Main Image */}
            <div className="absolute inset-0">
              <img
                src={vehicles[currentSlide]?.image_url}
                alt={`${vehicles[currentSlide]?.brand} ${vehicles[currentSlide]?.model}`}
                className="w-full h-full object-cover transition-opacity duration-500"
                data-testid="carousel-image"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-carbon-bg via-transparent to-carbon-bg/50" />
              <div className="absolute inset-0 bg-gradient-to-r from-carbon-bg/80 via-transparent to-transparent" />
            </div>

            {/* Vehicle Info */}
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16 z-10">
              <div className="max-w-7xl mx-auto">
                <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4 animate-fade-up">
                  Véhicule importé
                </p>
                <h1 className="font-unbounded text-4xl md:text-6xl font-black text-white mb-4 animate-fade-up delay-100">
                  {vehicles[currentSlide]?.brand}
                  <span className="text-neon-lime"> {vehicles[currentSlide]?.model}</span>
                </h1>
                <div className="flex flex-wrap items-center gap-6 mb-6 animate-fade-up delay-200">
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Calendar className="w-5 h-5 text-neon-lime" />
                    <span className="font-mono">{vehicles[currentSlide]?.year}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-300">
                    <span className="text-2xl">{getOriginFlag(vehicles[currentSlide]?.origin)}</span>
                    <span>{vehicles[currentSlide]?.origin}</span>
                  </div>
                </div>
                <p className="text-zinc-400 text-lg max-w-xl mb-8 animate-fade-up delay-300">
                  {vehicles[currentSlide]?.description}
                </p>
                
                {/* Carousel Controls */}
                <div className="flex items-center gap-4 animate-fade-up delay-400">
                  <button
                    onClick={prevSlide}
                    className="w-12 h-12 border border-zinc-700 flex items-center justify-center text-white hover:border-neon-lime hover:text-neon-lime transition-colors"
                    data-testid="carousel-prev"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <div className="flex items-center gap-2">
                    {vehicles.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-3 h-3 transition-all duration-300 ${
                          index === currentSlide
                            ? "bg-neon-lime scale-125"
                            : "bg-zinc-700 hover:bg-zinc-500"
                        }`}
                        data-testid={`carousel-dot-${index}`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={nextSlide}
                    className="w-12 h-12 border border-zinc-700 flex items-center justify-center text-white hover:border-neon-lime hover:text-neon-lime transition-colors"
                    data-testid="carousel-next"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <span className="font-mono text-zinc-500 ml-4">
                    {currentSlide + 1} / {vehicles.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center h-[80vh]">
            <div className="w-12 h-12 border-2 border-neon-lime border-t-transparent animate-spin" />
          </div>
        )}
      </section>

      {/* Gallery Grid */}
      <section className="py-24 md:py-32 relative">
        {/* Carbon texture */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none" 
          style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} 
        />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
              Nos Réalisations
            </p>
            <h2 className="font-unbounded text-3xl md:text-5xl font-bold text-white mb-4">
              Véhicules <span className="text-neon-lime">Importés</span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Découvrez une sélection de véhicules que nous avons importés pour nos clients satisfaits.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
            {vehicles.map((vehicle, index) => (
              <div
                key={vehicle.id}
                data-testid={`gallery-card-${index}`}
                className="gallery-card group relative bg-zinc-900/50 border border-zinc-800 overflow-hidden cursor-pointer"
                onClick={() => setCurrentSlide(index)}
              >
                {/* Image */}
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={vehicle.image_url}
                    alt={`${vehicle.brand} ${vehicle.model}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                
                {/* Overlay */}
                <div className="gallery-overlay absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex flex-col justify-end p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{getOriginFlag(vehicle.origin)}</span>
                    <span className="font-mono text-neon-lime text-sm">{vehicle.year}</span>
                  </div>
                  <h3 className="font-unbounded text-xl font-bold text-white">
                    {vehicle.brand} <span className="text-neon-lime">{vehicle.model}</span>
                  </h3>
                  <p className="text-zinc-400 text-sm mt-2">{vehicle.description}</p>
                </div>
                
                {/* Badge */}
                <div className="absolute top-4 right-4 bg-neon-lime text-black font-mono text-xs font-bold px-3 py-1">
                  {vehicle.origin}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Videos Section */}
      {videos.length > 0 && (
        <section className="py-24 md:py-32 relative border-t border-zinc-800" data-testid="videos-section">
          <div
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}}
          />
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <div className="text-center mb-16">
              <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
                En vidéo
              </p>
              <h2 className="font-unbounded text-3xl md:text-5xl font-bold text-white mb-4">
                Nos véhicules <span className="text-neon-lime">filmés</span>
              </h2>
              <p className="text-zinc-400 max-w-2xl mx-auto">
                Visite complète de nos imports, commentée par notre équipe.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="videos-grid">
              {videos.map((video) => (
                <div
                  key={video.id}
                  data-testid={`video-${video.id}`}
                  className="group bg-zinc-900/50 border border-zinc-800 overflow-hidden"
                >
                  <div className="relative aspect-video bg-black">
                    <video
                      ref={(el) => { if (el) videoRefs.current[video.id] = el; }}
                      src={`${API}/videos/${video.id}`}
                      className="w-full h-full object-cover"
                      preload="metadata"
                      playsInline
                      controls={activeVideo === video.id}
                      onClick={() => {
                        if (activeVideo !== video.id) {
                          setActiveVideo(video.id);
                          setTimeout(() => videoRefs.current[video.id]?.play(), 100);
                        }
                      }}
                    />
                    {activeVideo !== video.id && (
                      <button
                        onClick={() => {
                          setActiveVideo(video.id);
                          setTimeout(() => videoRefs.current[video.id]?.play(), 100);
                        }}
                        data-testid={`play-video-${video.id}`}
                        className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 transition-colors"
                        aria-label={`Lire la vidéo ${video.title}`}
                      >
                        <div className="w-16 h-16 bg-neon-lime rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(57,255,20,0.5)] group-hover:scale-110 transition-transform">
                          <Play className="w-7 h-7 text-black ml-1" fill="currentColor" />
                        </div>
                      </button>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-unbounded text-lg font-bold text-white">
                      {video.title}
                    </h3>
                    {video.description && (
                      <p className="text-zinc-400 text-sm mt-2">{video.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-carbon-paper border-t border-zinc-800 relative">
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none" 
          style={{backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')", backgroundSize: '200px'}} 
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="font-unbounded text-3xl md:text-5xl font-bold text-white mb-6">
            Votre prochain véhicule
            <br />
            <span className="text-neon-lime text-glow">vous attend</span>
          </h2>
          <p className="font-manrope text-zinc-400 text-lg max-w-2xl mx-auto mb-10">
            Vous avez repéré un véhicule en Europe ou en Corée du Sud ? 
            Utilisez notre simulateur pour estimer le coût total de votre import.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/simulateur">
              <Button
                data-testid="gallery-simulator-cta"
                className="bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider px-10 py-6 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)] text-base"
              >
                <span className="flex items-center gap-2">
                  Simuler mon import
                  <ArrowRight className="w-5 h-5" />
                </span>
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                data-testid="gallery-contact-cta"
                variant="outline"
                className="border-zinc-700 text-white font-unbounded font-bold uppercase tracking-wider px-10 py-6 hover:bg-zinc-800 text-base"
              >
                <span>Me faire accompagner</span>
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
